import type { FC, ReactNode } from 'react';
import React, { useContext, useCallback } from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/button';

import {
	ReactionLocation,
	ReactionContainerType,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { Reactions } from '@confluence/reactions/entry-points/Reactions.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { getCommentLink } from '../feedCard-helpers.ts';
import { FeedCardContext } from '../FeedCard/FeedCardContext.ts';
import type { CommentField as CommentType } from '../../feedCardTypes.ts';

const i18n = defineMessages({
	viewComment: {
		id: 'feed.card.comment.viewComment',
		defaultMessage: 'View comment',
		description: 'link that takes user to another page to view the comment',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReactionsWrapper = styled(FlexWrapper)<{
	isGlobalReactionsAndLikesEnabled?: boolean;
}>({
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: (props) =>
		props.isGlobalReactionsAndLikesEnabled ? 'space-between' : 'flex-end',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewCommentWrapper = styled.div({
	display: 'flex',
	paddingLeft: token('space.025'),
	justifyContent: 'right',
	minWidth: '100px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewCommentWrapperTest = styled.div({
	display: 'flex',
	paddingLeft: token('space.025'),
	justifyContent: 'left',
	minWidth: '100px',
});

type CommentProps = {
	comment: CommentType;
	pageId?: string;
	contentType?: string;
};

const CommentInfoLineComponent: FC<CommentProps & WrappedComponentProps> = ({
	comment,
	pageId,
	contentType,
}) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { isReactionsEnabled: isGlobalReactionsAndLikesEnabled } = useContext(ReactionsContext);

	const url = getCommentLink(comment);
	const commentId = comment.id;

	const onViewCommentClick = useCallback(() => {
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'feedCardViewComment',
			commentId,
			includeNavdex: true,
		});
	}, [fireAnalyticsEvent, commentId]);

	const containerType =
		contentType === 'page' ? ReactionContainerType.PAGE : ReactionContainerType.BLOGPOST;

	const renderReactionsAndComments = (): ReactNode | null => {
		if (commentId && pageId && contentType) {
			if (
				expValEquals(
					'confluence_following_feed_remove_social_proof',
					'cohort',
					'remove-emojis-and-comments-counts',
				) ||
				expValEquals('confluence_following_feed_remove_social_proof', 'cohort', 'remove-emojis')
			) {
				return (
					<>
						<ViewCommentWrapperTest>
							<Button
								href={url}
								onClick={onViewCommentClick}
								appearance="subtle-link"
								spacing="none"
							>
								<FormattedMessage {...i18n.viewComment} />
							</Button>
						</ViewCommentWrapperTest>
					</>
				);
			}

			return (
				<>
					<ReactionsWrapper
						data-testid="feed-comment-reactions"
						isGlobalReactionsAndLikesEnabled={isGlobalReactionsAndLikesEnabled}
					>
						{isGlobalReactionsAndLikesEnabled && (
							<Reactions
								contentId={commentId}
								location={ReactionLocation.FEED_COMMENT}
								containerId={pageId}
								containerType={containerType}
							/>
						)}
						<ViewCommentWrapper>
							<Button
								href={url}
								onClick={onViewCommentClick}
								appearance="subtle-link"
								spacing="none"
							>
								<FormattedMessage {...i18n.viewComment} />
							</Button>
						</ViewCommentWrapper>
					</ReactionsWrapper>
				</>
			);
		}

		return null;
	};

	return <small>{renderReactionsAndComments()}</small>;
};

export const CommentInfoLine: FC<WithIntlProps<CommentProps & WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<CommentProps & WrappedComponentProps>;
} = injectIntl(CommentInfoLineComponent);
